import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment-timezone";
import Select from "react-select";

import "flatpickr/dist/themes/material_green.css";
import Flatpickr from "flatpickr";
import { Spanish } from "flatpickr/dist/l10n/es.js";

import Header from "../components/Header";
import ScrollToTop from "../components/ScrollTop";
import PhoneLoader from "../components/loader/PhoneLoader";
import { useAuth } from "../provider/authProvider";

import "../Comisiones.css";
import "../ComisionesSedes.css";

const TZ = "America/Lima";
const MIN_DATE = "2025-07-11";

const SEGMENTS = {
    total: { key: "total", label: "Todos", color: "#4d0084" },
    payjoy: { key: "payjoy", label: "PayJoy", color: "#12b7ef" },
    efectiva: { key: "efectiva", label: "Efectiva", color: "#f4a300" },
    movilespay: { key: "movilespay", label: "MovilesPay", color: "#4d0084" },
    credismart: { key: "credismart", label: "CrediSmart", color: "#7c3aed" },
    aloCredit: { key: "aloCredit", label: "Alo Credit", color: "#FA8514" },
    contado: { key: "contado", label: "Contado", color: "#13b981" },
};

function getCommissionDefaultRange() {
    const today = moment().tz(TZ);
    const start =
        today.date() >= 26
            ? today.clone().date(26)
            : today.clone().subtract(1, "month").date(26);

    return {
        start: start.format("YYYY-MM-DD"),
        end: today.format("YYYY-MM-DD"),
    };
}

function formatDateText(start, end) {
    const s = moment.tz(start, "YYYY-MM-DD", TZ).locale("es");
    const e = moment.tz(end, "YYYY-MM-DD", TZ).locale("es");

    if (!s.isValid() || !e.isValid()) return "Mostrando fechas seleccionadas";

    if (s.isSame(e, "day")) {
        return `Mostrando ${s.format("dddd, D [de] MMMM")}`;
    }

    return `Mostrando ${s.format("dddd, D [de] MMMM")} al ${e.format("dddd, D [de] MMMM")}`;
}

function onlyNumbers(value) {
    return String(value || "")
        .replace(/\D/g, "")
        .slice(0, 4);
}

function getCrediSmartSales(advisor) {
    return Number(
        advisor.credismart ?? advisor.crediSmart ?? advisor.creditSmart ?? 0,
    );
}

function getAloCreditSales(advisor) {
    return Number(
        advisor.aloCredit ?? advisor.alocredit ?? advisor.alo_credit ?? 0,
    );
}

function getAdvisorTotalSales(advisor) {
    const segmentedTotal =
        Number(advisor.payjoy || 0) +
        Number(advisor.efectiva || 0) +
        Number(advisor.movilespay || 0) +
        getCrediSmartSales(advisor) +
        getAloCreditSales(advisor) +
        Number(advisor.contado || 0);

    const backendTotal = Number(advisor.totalSales || 0);

    return Math.max(backendTotal, segmentedTotal);
}

function getSalesBySegment(advisor, activeSegment) {
    if (activeSegment === "payjoy") return Number(advisor.payjoy || 0);
    if (activeSegment === "efectiva") return Number(advisor.efectiva || 0);
    if (activeSegment === "movilespay") return Number(advisor.movilespay || 0);
    if (activeSegment === "credismart") return getCrediSmartSales(advisor);
    if (activeSegment === "aloCredit") return getAloCreditSales(advisor);
    if (activeSegment === "contado") return Number(advisor.contado || 0);
    return getAdvisorTotalSales(advisor);
}

function getPerformanceColor(percent) {
    if (percent >= 100) return "#2563eb";
    if (percent > 75) return "#16a34a";
    if (percent >= 51) return "#f59e0b";
    return "#dc2626";
}

function formatPEN(value) {
    return new Intl.NumberFormat("es-PE", {
        style: "currency",
        currency: "PEN",
        minimumFractionDigits: 2,
    }).format(Number(value || 0));
}

function getCityTotalSales(cityItem) {
    const advisors = cityItem.advisors || [];

    const segmentedTotal = advisors.reduce((acc, advisor) => {
        return acc + getAdvisorTotalSales(advisor);
    }, 0);

    const backendTotal = Number(cityItem.totalSales || 0);

    return Math.max(backendTotal, segmentedTotal);
}

function getGoalPercent(sales, goal) {
    const safeSales = Math.max(Number(sales || 0), 0);
    const safeGoal = Math.max(Number(goal || 0), 0);
    if (safeGoal <= 0) return 0;
    return Math.round((safeSales / safeGoal) * 100);
}

function calculateCommissionByScale(sales, goal) {
    const safeSales = Math.max(Number(sales || 0), 0);
    const safeGoal = Math.max(Number(goal || 0), 1);

    const tier1Limit = Math.floor(safeGoal * 0.5);
    const tier2Limit = Math.floor(safeGoal * 0.75);
    const tier3Limit = safeGoal;

    const tier1Sales = Math.min(safeSales, tier1Limit);
    const tier2Sales = Math.max(Math.min(safeSales, tier2Limit) - tier1Limit, 0);
    const tier3Sales = Math.max(Math.min(safeSales, tier3Limit) - tier2Limit, 0);
    const tier4Sales = Math.max(safeSales - tier3Limit, 0);

    return {
        total: tier1Sales * 5 + tier2Sales * 10 + tier3Sales * 20 + tier4Sales * 30,
    };
}

function CommissionRange({ value, goal }) {
    const safeGoal = Math.max(Number(goal || 0), 1);
    const safeValue = Math.max(Number(value || 0), 0);

    const realPercent = Math.round((safeValue / safeGoal) * 100);
    const visualPercent = Math.min(realPercent, 100);
    const overflowPercent = Math.max(realPercent - 100, 0);
    const overflowSize = Math.min(overflowPercent * 1.2, 90);

    const performanceColor = getPerformanceColor(realPercent);
    const isOverGoal = realPercent >= 100;

    return (
        <div className="commissionRangeBox">
            <div
                className={`commissionRangeTrack ${isOverGoal ? "isOverGoal" : ""}`}
                style={{
                    "--rangePercent": `${visualPercent}%`,
                    "--realPercent": `${realPercent}%`,
                    "--rangeColor": performanceColor,
                    "--overflowSize": `${overflowSize}px`,
                }}
            >
                <span className="rangeThumb rangeThumbLeft">
                    <span className="rangePercentBadge">{realPercent}%</span>
                </span>

                <span className="rangeThumb rangeThumbRight" />

                {isOverGoal && (
                    <span className="rangeOverGoalBadge">+{overflowPercent}%</span>
                )}
            </div>

            <div className="commissionRangeLabels">
                <span>0</span>
                <strong>
                    {safeValue} - {safeGoal}
                </strong>
                <span>{isOverGoal ? `${safeGoal}+` : safeGoal}</span>
            </div>

            {isOverGoal && (
                <div className="commissionOverGoalText">
                    Meta superada: {safeValue} ventas de {safeGoal}
                </div>
            )}
        </div>
    );
}

function AdvisorCard({ advisor, goal, onGoalChange, onGoalSave, canManageGoals = true }) {
    const [activeSegment, setActiveSegment] = useState("total");

    const activeConfig = SEGMENTS[activeSegment] || SEGMENTS.total;
    const activeSales = getSalesBySegment(advisor, activeSegment);

    const goalNumber = Math.max(Number(goal || 0), 1);
    const percent = Math.round((activeSales / goalNumber) * 100);
    const reachedGoal = percent >= 100;

    const commission = calculateCommissionByScale(activeSales, goalNumber);
    const fixedCommission = reachedGoal ? 70 : 0;

    const segmentButtons = [
        "total",
        "payjoy",
        "efectiva",
        "movilespay",
        "credismart",
        "aloCredit",
        "contado",
    ];

    return (
        <article
            className={`commissionAdvisorCard ${reachedGoal ? "overGoal" : ""}`}
            style={{ "--activeColor": activeConfig.color }}
        >
            <div className="commissionCardTop">
                <div className="commissionSegmentMenu">
                    {segmentButtons.map((key) => (
                        <button
                            key={key}
                            type="button"
                            className={`segmentBtn ${activeSegment === key ? "active" : ""}`}
                            onClick={() => setActiveSegment(key)}
                        >
                            {SEGMENTS[key].label}{" "}
                            <strong>
                                {key === "total"
                                    ? getAdvisorTotalSales(advisor)
                                    : key === "credismart"
                                        ? getCrediSmartSales(advisor)
                                        : key === "aloCredit"
                                            ? getAloCreditSales(advisor)
                                            : Number(advisor[key] || 0)}
                            </strong>
                        </button>
                    ))}
                </div>

                <div className="commissionTopRightBox">
                    {reachedGoal && (
                        <div className="commissionAmountBox">
                            <span>Comisión Sueldo</span>
                            <strong>{formatPEN(fixedCommission)}</strong>
                        </div>
                    )}

                    <div className="commissionAmountBox">
                        <span>Comisión Cuotas</span>
                        <strong>{formatPEN(commission.total)}</strong>
                    </div>

                    <div className={`commissionGoalBox ${!canManageGoals ? "readOnly" : ""}`}>
                        <span>Cuota mensual</span>
                        {canManageGoals ? (
                            <input
                                type="text"
                                inputMode="numeric"
                                value={goal}
                                onChange={(e) =>
                                    onGoalChange(advisor.advisorId, onlyNumbers(e.target.value))
                                }
                                onBlur={(e) =>
                                    onGoalSave(advisor.advisorId, onlyNumbers(e.target.value))
                                }
                                placeholder="0"
                            />
                        ) : (
                            <strong>{Number(goal || advisor.monthlyGoal || 0)}</strong>
                        )}
                    </div>
                </div>
            </div>

            <div className="commissionCardBody">
                <div className="commissionAdvisorInfo">
                    <span className="advisorCity">{advisor.cityName}</span>
                    <h4>{advisor.advisorName}</h4>
                    <p>{advisor.sedeName}</p>
                </div>

                <CommissionRange value={activeSales} goal={goal} />

                <div className="commissionTotalBox">
                    <span>{activeConfig.label}</span>
                    <strong>{activeSales}</strong>
                </div>
            </div>
        </article>
    );
}


function SedeCard({
    sede,
    goals,
    onGoalChange,
    onGoalSave,
    canManageGoals,
}) {
    const advisors = sede.advisors || [];
    const totalGoal = advisors.reduce(
        (acc, advisor) =>
            acc + Number(goals[advisor.advisorId] ?? advisor.monthlyGoal ?? 0),
        0,
    );
    const totalSales = advisors.reduce(
        (acc, advisor) => acc + getAdvisorTotalSales(advisor),
        0,
    );

    return (
        <article className="commissionSedeCard">
            <header className="commissionSedeCardHeader">
                <div>
                    <span>{sede.cityName}</span>
                    <h4>{sede.sedeName}</h4>
                </div>
                <div className="commissionSedeTotals">
                    <div>
                        <span>Cuota sede</span>
                        <strong>{totalGoal}</strong>
                    </div>
                    <div>
                        <span>Ventas</span>
                        <strong>{Math.max(Number(sede.totalSales || 0), totalSales)}</strong>
                    </div>
                </div>
            </header>

            <div className="commissionSedeAdvisorList">
                {advisors.length === 0 && (
                    <div className="commissionSedeEmpty">No hay asesores asignados.</div>
                )}

                {advisors.map((advisor) => {
                    const goalValue =
                        goals[advisor.advisorId] ?? String(advisor.monthlyGoal || 0);
                    const sales = getAdvisorTotalSales(advisor);
                    const safeGoal = Math.max(Number(goalValue || 0), 1);
                    const percent = Math.round((sales / safeGoal) * 100);

                    return (
                        <div className="commissionSedeAdvisorRow" key={advisor.advisorId}>
                            <div className="commissionSedeAdvisorIdentity">
                                <strong>{advisor.advisorName}</strong>
                                <span>{sales} ventas · {percent}%</span>
                            </div>

                            <div className="commissionSedeGoalField">
                                <span>Cuota</span>
                                {canManageGoals ? (
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={goalValue}
                                        onChange={(event) =>
                                            onGoalChange(
                                                advisor.advisorId,
                                                onlyNumbers(event.target.value),
                                            )
                                        }
                                        onBlur={(event) =>
                                            onGoalSave(
                                                advisor.advisorId,
                                                onlyNumbers(event.target.value),
                                            )
                                        }
                                    />
                                ) : (
                                    <strong>{Number(goalValue || 0)}</strong>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </article>
    );
}

export default function Comisiones() {
    const { user, rol } = useAuth();
    const roleStr = rol != null ? String(rol) : String(localStorage.getItem("rol") || "");
    const isAdminRole = roleStr === "1";
    const isAdvisorRole = roleStr === "3";
    const canManageGoals = isAdminRole;

    const defaultRange = useMemo(() => getCommissionDefaultRange(), []);
    const today = useMemo(() => moment().tz(TZ).format("YYYY-MM-DD"), []);

    const [loading, setLoading] = useState(false);
    const [cities, setCities] = useState([]);
    const [viewMode, setViewMode] = useState("advisors");
    const [goalPeriod, setGoalPeriod] = useState({
        start: defaultRange.start,
        end: moment.tz(defaultRange.start, "YYYY-MM-DD", TZ).add(1, "month").subtract(1, "day").format("YYYY-MM-DD"),
    });
    const [isCurrentPeriod, setIsCurrentPeriod] = useState(true);

    const [advisorOptions, setAdvisorOptions] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);

    const [advisorId, setAdvisorId] = useState("0");
    const [city, setCity] = useState("0");
    const [orderSales, setOrderSales] = useState("desc");

    const [start, setStart] = useState(defaultRange.start);
    const [end, setEnd] = useState(defaultRange.end);
    const [showText, setShowText] = useState(
        formatDateText(defaultRange.start, defaultRange.end),
    );

    const [goals, setGoals] = useState({});

    const visibleSummary = useMemo(() => {
        return (cities || []).reduce(
            (acc, cityItem) => {
                const cityGoal = (cityItem.advisors || []).reduce((sum, advisor) => {
                    return sum + Number(goals[advisor.advisorId] ?? advisor.monthlyGoal ?? 0);
                }, 0);
                const citySales = getCityTotalSales(cityItem);

                acc.totalGoal += cityGoal;
                acc.totalSales += citySales;
                return acc;
            },
            { totalGoal: 0, totalSales: 0 },
        );
    }, [cities, goals]);

    const visibleSalesPercent = useMemo(
        () => getGoalPercent(visibleSummary.totalSales, visibleSummary.totalGoal),
        [visibleSummary],
    );

    const calendarInputRef = useRef(null);
    const calendarInstanceRef = useRef(null);

    const api = useMemo(
        () =>
            axios.create({
                baseURL: process.env.REACT_APP_API,
                timeout: 30000,
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            }),
        [],
    );

    const selectStyles = useMemo(
        () => ({
            control: (base, state) => ({
                ...base,
                minHeight: 44,
                borderRadius: 14,
                borderColor: state.isFocused
                    ? "rgba(77,0,132,.45)"
                    : "rgba(203,213,225,.95)",
                boxShadow: state.isFocused
                    ? "0 0 0 4px rgba(77,0,132,.10)"
                    : "0 8px 20px rgba(15,23,42,.04)",
                fontSize: 13,
                fontWeight: 700,
            }),
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            option: (base, state) => ({
                ...base,
                fontSize: 13,
                fontWeight: state.isSelected ? 900 : 700,
                background: state.isSelected
                    ? "rgba(77,0,132,.10)"
                    : state.isFocused
                        ? "rgba(77,0,132,.06)"
                        : "#fff",
                color: state.isSelected ? "#4d0084" : "#1e293b",
            }),
        }),
        [],
    );

    const orderOptions = [
        { value: "desc", label: "Ventas: mayor a menor" },
        { value: "asc", label: "Ventas: menor a mayor" },
    ];

    const selectedAdvisor = useMemo(
        () =>
            advisorOptions.find((item) => String(item.value) === String(advisorId)) ||
            advisorOptions[0] ||
            null,
        [advisorOptions, advisorId],
    );

    const selectedCity = useMemo(
        () =>
            cityOptions.find((item) => String(item.value) === String(city)) ||
            cityOptions[0] ||
            null,
        [cityOptions, city],
    );

    const selectedOrder = useMemo(
        () =>
            orderOptions.find((item) => item.value === orderSales) || orderOptions[0],
        [orderSales],
    );

    async function fetchMeta() {
        const res = await api.get("/comisiones/meta");
        const data = res?.data || {};

        setAdvisorOptions([
            { value: "0", label: "Todos los asesores" },
            ...(data.advisors || []).map((item) => ({
                value: String(item.value),
                label: item.label,
            })),
        ]);

        setCityOptions([
            { value: "0", label: "Todas las ciudades" },
            ...(data.cities || []).map((item) => ({
                value: String(item.label),
                label: item.label,
            })),
        ]);
    }

    async function fetchComisiones(overrides = {}) {
        const params = {
            start: overrides.start ?? start,
            end: overrides.end ?? end,
            advisor_id: overrides.advisor_id ?? advisorId,
            city: overrides.city ?? city,
            order_sales: overrides.order_sales ?? orderSales,
        };

        const res = await api.get("/comisiones/list", { params });
        const responseIsCurrentPeriod = res?.data?.isCurrentPeriod !== false;
        const advisorIsVisible = (advisor) => {
            const hasValidSede =
                Number(advisor.sedeId || 0) > 0 && advisor.sedeName !== "Sin sede";
            const hasSales = getAdvisorTotalSales(advisor) > 0;

            return hasValidSede && (responseIsCurrentPeriod || hasSales);
        };

        const list = (res?.data?.cities || [])
            .map((cityItem) => {
                const advisors = (cityItem.advisors || []).filter(advisorIsVisible);

                const sedes = (cityItem.sedes || [])
                    .filter((sedeItem) => Number(sedeItem.sedeId || 0) > 0)
                    .map((sedeItem) => ({
                        ...sedeItem,
                        advisors: (sedeItem.advisors || []).filter(advisorIsVisible),
                    }))
                    .filter((sedeItem) => sedeItem.advisors.length > 0);

                return { ...cityItem, advisors, sedes };
            })
            .filter((cityItem) => cityItem.advisors.length > 0);
        const returnedPeriod = res?.data?.goalPeriod;

        setCities(list);
        setIsCurrentPeriod(responseIsCurrentPeriod);
        if (returnedPeriod?.start && returnedPeriod?.end) {
            setGoalPeriod(returnedPeriod);
        }

        setGoals(() => {
            const next = {};
            for (const cityItem of list) {
                for (const advisor of cityItem.advisors || []) {
                    next[advisor.advisorId] = String(advisor.monthlyGoal || 60);
                }
            }
            return next;
        });
    }

    async function saveAdvisorGoal(advisorIdValue, value) {
        try {
            const cleanGoal = onlyNumbers(value);

            if (!canManageGoals) return;

            await api.post("/comisiones/goal", {
                advisor_id: advisorIdValue,
                goal: Number(cleanGoal || 0),
                period_start: goalPeriod.start,
                period_end: goalPeriod.end,
            });

            setCities((prev) =>
                prev.map((cityItem) => {
                    const advisors = (cityItem.advisors || []).map((advisor) => {
                        if (Number(advisor.advisorId) !== Number(advisorIdValue))
                            return advisor;

                        return {
                            ...advisor,
                            monthlyGoal: Number(cleanGoal || 0),
                        };
                    });

                    return {
                        ...cityItem,
                        totalGoal: advisors.reduce((acc, advisor) => {
                            return (
                                acc +
                                Number(goals[advisor.advisorId] ?? advisor.monthlyGoal ?? 0)
                            );
                        }, 0),
                        advisors,
                    };
                }),
            );
        } catch (error) {
            console.error(error);
            alert(error?.response?.data?.error || "Error guardando cuota del asesor");
        }
    }

    async function init() {
        setLoading(true);
        try {
            await fetchMeta();
            await fetchComisiones({
                start: defaultRange.start,
                end: defaultRange.end,
                advisor_id: "0",
                city: "0",
                order_sales: "desc",
            });
        } catch (error) {
            console.error(error);
            alert(error?.response?.data?.error || "Error cargando comisiones");
        } finally {
            setLoading(false);
        }
    }

    async function handleFilterChange(patch) {
        const next = {
            advisor_id: patch.advisor_id ?? advisorId,
            city: patch.city ?? city,
            order_sales: patch.order_sales ?? orderSales,
            start: patch.start ?? start,
            end: patch.end ?? end,
        };

        if (patch.advisor_id !== undefined) setAdvisorId(patch.advisor_id);
        if (patch.city !== undefined) setCity(patch.city);
        if (patch.order_sales !== undefined) setOrderSales(patch.order_sales);
        if (patch.start !== undefined) setStart(patch.start);
        if (patch.end !== undefined) setEnd(patch.end);

        if (patch.start !== undefined || patch.end !== undefined) {
            setShowText(formatDateText(next.start, next.end));
        }

        setLoading(true);
        try {
            await fetchComisiones(next);
        } catch (error) {
            console.error(error);
            alert(error?.response?.data?.error || "Error cargando comisiones");
        } finally {
            setLoading(false);
        }
    }

    function handleGoalChange(advisorIdValue, value) {
        setGoals((prev) => ({
            ...prev,
            [advisorIdValue]: value,
        }));
    }

    function getCityGoal(cityItem) {
        return (cityItem.advisors || []).reduce((acc, advisor) => {
            return acc + Number(goals[advisor.advisorId] || advisor.monthlyGoal || 0);
        }, 0);
    }


    useEffect(() => {
        if (!isAdminRole && viewMode !== "advisors") {
            setViewMode("advisors");
        }
    }, [isAdminRole, viewMode]);

    useEffect(() => {
        moment.tz.setDefault(TZ);
        moment.locale("es");
        Flatpickr.localize(Spanish);

        init();

        const input = calendarInputRef.current;

        if (input && !calendarInstanceRef.current) {
            calendarInstanceRef.current = Flatpickr(input, {
                mode: "range",
                dateFormat: "Y-m-d",
                defaultDate: [defaultRange.start, defaultRange.end],
                allowInput: true,
                minDate: MIN_DATE,
                maxDate: today,
                locale: Spanish,
                onChange: (selectedDates) => {
                    if (!selectedDates || selectedDates.length === 0) {
                        setStart(defaultRange.start);
                        setEnd(defaultRange.end);
                        setShowText(formatDateText(defaultRange.start, defaultRange.end));

                        handleFilterChange({
                            start: defaultRange.start,
                            end: defaultRange.end,
                        });
                        return;
                    }

                    if (selectedDates.length === 1) {
                        const d = moment(selectedDates[0]).tz(TZ).format("YYYY-MM-DD");

                        if (calendarInputRef.current) {
                            calendarInputRef.current.value = d;
                        }

                        return;
                    }

                    const d1 = moment(selectedDates[0]).tz(TZ).format("YYYY-MM-DD");
                    const d2 = moment(selectedDates[1]).tz(TZ).format("YYYY-MM-DD");

                    const nextStart = d1 <= d2 ? d1 : d2;
                    const nextEnd = d1 <= d2 ? d2 : d1;

                    setStart(nextStart);
                    setEnd(nextEnd);
                    setShowText(formatDateText(nextStart, nextEnd));

                    handleFilterChange({
                        start: nextStart,
                        end: nextEnd,
                    });
                },
            });
        }

        return () => {
            try {
                calendarInstanceRef.current?.destroy?.();
            } catch {}
            calendarInstanceRef.current = null;
        };
    }, []);

    return (
        <div>
            <Header user={user} namePage="Comisiones" />
            <ScrollToTop />

            <section className="sectionBody sectionBodyComisiones">
                <div className="containerCustom containerBody">
                    <div className="blockBody">
                        <div className="commissionHeader">
                            <div>
                                <h2>Comisiones</h2>
                                <p>{showText}</p>
                            </div>

                            {isAdminRole && (
                                <div className="commissionViewSwitch" role="group" aria-label="Cambiar vista">
                                    <button
                                        type="button"
                                        className={viewMode === "advisors" ? "active" : ""}
                                        onClick={() => setViewMode("advisors")}
                                    >
                                        Vista por asesor
                                    </button>
                                    <button
                                        type="button"
                                        className={viewMode === "sedes" ? "active" : ""}
                                        onClick={() => setViewMode("sedes")}
                                    >
                                        Vista por sedes
                                    </button>
                                </div>
                            )}

                            {!isAdvisorRole && (
                                <div className="commissionFilters">
                                    <div className="commissionFilterItem">
                                        <span>Asesor</span>
                                        <Select
                                            value={selectedAdvisor}
                                            options={advisorOptions}
                                            onChange={(opt) =>
                                                handleFilterChange({
                                                    advisor_id: String(opt?.value || "0"),
                                                })
                                            }
                                            styles={selectStyles}
                                            menuPortalTarget={document.body}
                                        />
                                    </div>

                                    <div className="commissionFilterItem">
                                        <span>Ciudad</span>
                                        <Select
                                            value={selectedCity}
                                            options={cityOptions}
                                            onChange={(opt) =>
                                                handleFilterChange({
                                                    city: String(opt?.value || "0"),
                                                })
                                            }
                                            styles={selectStyles}
                                            menuPortalTarget={document.body}
                                        />
                                    </div>

                                    <div className="commissionFilterItem">
                                        <span>Orden</span>
                                        <Select
                                            value={selectedOrder}
                                            options={orderOptions}
                                            onChange={(opt) =>
                                                handleFilterChange({
                                                    order_sales: opt?.value || "desc",
                                                })
                                            }
                                            styles={selectStyles}
                                            menuPortalTarget={document.body}
                                        />
                                    </div>

                                    <div className="commissionFilterItem">
                                        <span>Calendario</span>
                                        <input
                                            ref={calendarInputRef}
                                            type="text"
                                            className="commissionCalendarInput"
                                            placeholder="Seleccionar fechas"
                                            readOnly
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {isAdminRole && (
                            <div className="commissionPeriodNotice">
                                <span>Periodo de cuota seleccionado</span>
                                <strong>
                                    {moment.tz(goalPeriod.start, "YYYY-MM-DD", TZ).format("DD/MM/YYYY")}
                                    {" — "}
                                    {moment.tz(goalPeriod.end, "YYYY-MM-DD", TZ).format("DD/MM/YYYY")}
                                </strong>
                                <small>Las cuotas quedan guardadas históricamente por cada periodo del 26 al 25.</small>
                            </div>
                        )}

                        {isAdminRole && !loading && cities.length > 0 && (
                            <section className="commissionVisibleSummary" aria-label="Resumen de ciudades visibles">
                                <div>
                                    <span>Cuota total de ciudades visibles</span>
                                    <strong>{visibleSummary.totalGoal}</strong>
                                </div>
                                <div>
                                    <span>Ventas totales visibles</span>
                                    <strong>{visibleSummary.totalSales}</strong>
                                </div>
                                <div className="commissionVisibleSummaryPercent">
                                    <span>Porcentaje total</span>
                                    <strong>{visibleSalesPercent}%</strong>
                                </div>
                            </section>
                        )}

                        {loading && (
                            <div className="commissionLoader">
                                <PhoneLoader />
                            </div>
                        )}

                        {!loading && cities.length === 0 && (
                            <div className="commissionEmpty">
                                No se encontraron asesores para los filtros seleccionados.
                            </div>
                        )}

                        {!loading && viewMode === "advisors" &&
                            cities.map((cityItem) => (
                                <section className="commissionCitySection" key={cityItem.city}>
                                    <div className="commissionCityHeader">
                                        <div>
                                            <h3>Ciudad ({cityItem.city})</h3>
                                            <p>{getCityTotalSales(cityItem)} ventas</p>
                                        </div>

                                        {!isAdvisorRole && (() => {
                                            const cityGoal = getCityGoal(cityItem);
                                            const citySales = getCityTotalSales(cityItem);
                                            const cityPercent = getGoalPercent(citySales, cityGoal);

                                            return (
                                                <div className="commissionCityMetricsInline">
                                                    <div className="commissionCityGoal">
                                                        <span>Cuota ciudad</span>
                                                        <strong>{cityGoal}</strong>
                                                    </div>
                                                    <div className="commissionCityGoal">
                                                        <span>Ventas ciudad</span>
                                                        <strong>{citySales}</strong>
                                                    </div>
                                                    <div className="commissionCityGoal commissionCityPercent">
                                                        <span>Porcentaje</span>
                                                        <strong>{cityPercent}%</strong>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>

                                    <div className="commissionCardsList">
                                        {(cityItem.advisors || []).map((advisor) => (
                                            <AdvisorCard
                                                key={advisor.advisorId}
                                                advisor={advisor}
                                                goal={goals[advisor.advisorId] || ""}
                                                onGoalChange={handleGoalChange}
                                                onGoalSave={saveAdvisorGoal}
                                                canManageGoals={canManageGoals}
                                            />
                                        ))}
                                    </div>
                                </section>
                            ))}

                        {!loading && isAdminRole && viewMode === "sedes" && (
                            <div className="commissionSedeView">
                                {cities.map((cityItem) => (
                                    <section className="commissionSedeCitySection" key={cityItem.city}>
                                        <div className="commissionSedeCityHeader">
                                            <div>
                                                <span>Ciudad</span>
                                                <h3>{cityItem.city}</h3>
                                            </div>
                                            {(() => {
                                                const cityGoal = getCityGoal(cityItem);
                                                const citySales = getCityTotalSales(cityItem);
                                                const cityPercent = getGoalPercent(citySales, cityGoal);

                                                return (
                                                    <div className="commissionSedeCityMetrics">
                                                        <div>
                                                            <span>Cuota ciudad</span>
                                                            <strong>{cityGoal}</strong>
                                                        </div>
                                                        <div>
                                                            <span>Ventas ciudad</span>
                                                            <strong>{citySales}</strong>
                                                        </div>
                                                        <div className="commissionSedeCityPercent">
                                                            <span>Porcentaje</span>
                                                            <strong>{cityPercent}%</strong>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>

                                        <div className="commissionSedeGrid">
                                            {(cityItem.sedes || []).map((sedeItem) => (
                                                <SedeCard
                                                    key={`${cityItem.city}-${sedeItem.sedeId}-${sedeItem.sedeName}`}
                                                    sede={sedeItem}
                                                    goals={goals}
                                                    onGoalChange={handleGoalChange}
                                                    onGoalSave={saveAdvisorGoal}
                                                    canManageGoals={canManageGoals}
                                                />
                                            ))}
                                        </div>
                                    </section>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
